import React, { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQ = () => {
  const faqs = [
    {
      question: 'What is utility-scale solar power?',
      answer: 'Utility-scale solar power refers to large solar installations (typically 1 MW or larger) that generate electricity for distribution to the power grid. Unlike rooftop solar, these plants supply power directly to distribution companies (DISCOMs) or state utilities, serving thousands of consumers.'
    },
    {
      question: 'How does TenSolar ensure project quality and compliance?',
      answer: 'We follow a compliance-first approach with strict adherence to MNRE guidelines, State DISCOM regulations, and grid safety standards. Our projects undergo rigorous quality checks at every stage, from component selection to commissioning. We work with certified equipment suppliers and maintain detailed documentation for all regulatory requirements.'
    },
    {
      question: 'What is the expected lifespan of your solar power plant?',
      answer: 'Our solar power plants are designed for 25-30 years of operational life. Solar panels come with 25-year performance warranties, while other components like inverters and transformers are regularly maintained and upgraded as needed. With proper maintenance, plants can continue generating power beyond 30 years.'
    },
    {
      question: 'How does the 10 MW Madhya Pradesh project contribute to renewable energy goals?',
      answer: 'Our 10 MW plant generates approximately 15 million kWh of clean electricity annually, powering around 7,500 households. It reduces CO₂ emissions by over 12,000 tonnes per year and contributes directly to India\'s target of 500 GW renewable energy capacity by 2030.'
    },
    {
      question: 'What are the benefits for state governments and DISCOMs?',
      answer: 'Solar power helps states meet their Renewable Purchase Obligations (RPO), provides competitive electricity tariffs, reduces transmission losses through distributed generation, and supports energy security. DISCOMs benefit from long-term Power Purchase Agreements (PPAs) with predictable costs.'
    },
    {
      question: 'How does TenSolar handle land acquisition and local community engagement?',
      answer: 'We conduct thorough environmental and social impact assessments before land acquisition. We engage with local communities throughout the project lifecycle, provide employment opportunities during construction and operation, and ensure responsible land usage practices that benefit surrounding areas.'
    },
    {
      question: 'What monitoring systems are in place for performance tracking?',
      answer: 'Our plants feature 24/7 remote monitoring systems with weather station integration, real-time performance analytics, and predictive maintenance alerts. This ensures optimal generation, quick issue resolution, and transparent reporting to all stakeholders.'
    },
    {
      question: 'Can TenSolar develop projects in other states?',
      answer: 'Yes, while our flagship project is in Madhya Pradesh, we have the capability and expertise to develop utility-scale solar projects across India. We assess opportunities based on solar irradiation, grid infrastructure, regulatory environment, and state policy support.'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-emerald-700" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-gray-600">
            Common questions about TenSolar, our projects, and solar energy
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-gray-50 rounded-xl border border-gray-100 px-6 hover:bg-gray-100 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-emerald-700 py-6">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-6">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <button
            onClick={() => {
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-emerald-700 font-semibold hover:text-emerald-800 underline underline-offset-4"
          >
            Contact us for more information
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
